import { ChangeDetectionStrategy, Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BudgetvalidatorService } from '@services/filevalidators/budgetvalidator.service';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { aciesNumForm } from '@shared/helpers';
import { IInsightsBudgetResponse, InsightsService } from '@shared/services/insights.service';
import { Subject, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx';

@Component({
	selector: 'app-budgets',
	templateUrl: './budgets.component.html',
	styleUrls: ['./budgets.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class BudgetsComponent implements OnInit, OnDestroy {
	isLoading = true;
	showSearch = false;
	displayedColumns: string[] = ['sheetName', 'orderType', 'debitorId', 'debitorGroup', 'salesPerson', 'districtCode', 'jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

	dataSource = new MatTableDataSource();
	resultsLength = 0;
	budgetData: any[];
	template: {
		Ordretype: '1';
		DebitorID: '2';
	};

	searchText = '';

	private unsubscribe: Subject<void> = new Subject<void>();
	public aciesNumForm: (value: number, minNumberOfDigits?: number, maxNumberOfDigits?: number) => string;

	@ViewChild(MatPaginator) public paginator: MatPaginator;
	@ViewChild(MatSort) public sort: MatSort;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add_alt',
			text: () => 'insights.administration.budgets.buttons.upload',
			click: () => this.openUploadFile(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_budget_download',
			text: () => 'insights.administration.budgets.buttons.download',
			click: () => this.downloadBudget(),
		},
		{
			icon: () => 'acies_budget_template',
			text: () => 'insights.administration.budgets.buttons.template',
			click: () => this.downloadTemplate(),
		},
	];

	constructor(
		public dialog: MatDialog,
		private insightsService: InsightsService,
		private injector: Injector,
	) {}

	ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngOnInit(): void {
		this.aciesNumForm = aciesNumForm;

		this.insightsService
			.fetchBudget()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((x) => this.parseBudget(x));

		setTimeout(() => {
			this.dataSource.paginator = this.paginator;
			this.dataSource.sort = this.sort;
			this.sort.sortChange.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
				this.paginator.firstPage();
			});
		});
	}

	mapBudget(row: any): any {
		return {
			sheetName: row.sheetName,
			orderType: row.data.Ordretype,
			debitorId: row.data.DebitorID,
			debitorGroup: row.data.Debitorgruppe,
			salesPerson: row.data.Sælger,
			districtCode: row.data.Distriktkode,
			jan: row.data.Jan,
			feb: row.data.Feb,
			mar: row.data.Mar,
			apr: row.data.Apr,
			may: row.data.Maj,
			jun: row.data.Jun,
			jul: row.data.Jul,
			aug: row.data.Aug,
			sep: row.data.Sep,
			oct: row.data.Okt,
			nov: row.data.Nov,
			dec: row.data.Dec,
		};
	}

	parseBudget(budgetData: IInsightsBudgetResponse) {
		if (budgetData && budgetData.result) {
			this.budgetData = JSON.parse(JSON.parse(budgetData.result.data));
			const mappedBudget = this.budgetData.map((row) => this.mapBudget(row));
			this.dataSource.data = mappedBudget;
			this.dataSource.filterPredicate = function (data: any, filter: string): boolean {
				return data.sheetName.toLowerCase().includes(filter) || JSON.stringify(data).toLowerCase().includes(filter);
			};
			this.resultsLength = mappedBudget.length;
		} else {
			this.dataSource.data = [];
			this.resultsLength = 0;
		}
		this.isLoading = false;
	}

	openUploadFile() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'insights.administration.budgets.upload.title',
				subHeadingKey: 'insights.administration.budgets.upload.subtitle',
				allowedFileTypes: ['.xls', '.xlsx'],
				validatorService: this.injector.get(BudgetvalidatorService),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.insightsService
					.fetchBudget()
					.pipe(takeUntil(this.unsubscribe))
					.subscribe((x) => {
						this.budgetData = JSON.parse(JSON.parse(x.result.data));
						const mappedBudget = this.budgetData.map((row) => this.mapBudget(row));
						this.dataSource.data = mappedBudget;
						this.isLoading = false;
					});
			});
	}

	downloadBudget() {
		const wb = XLSX.utils.book_new();
		const sheetRows = new Map<string, string[]>();
		this.budgetData.forEach((element) => {
			if (sheetRows.has(element.sheetName)) {
				const newRow: string[] = sheetRows.get(element.sheetName);
				newRow.push(element.data);
				sheetRows.set(element.sheetName, newRow);
			} else {
				const newRow: string[] = [];
				newRow.push(element.data);
				sheetRows.set(element.sheetName, newRow);
			}
		});
		sheetRows.forEach((value, key) => {
			XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(value), key);
		});
		XLSX.writeFile(wb, 'budget.xlsx');
	}

	downloadTemplate() {
		const arr = [
			[
				{
					Ordretype: '',
					DebitorID: '',
					Debitorgruppe: '',
					Sælger: '',
					Distriktkode: '',
					Jan: 0,
					Feb: 0,
					Mar: 0,
					Apr: 0,
					Maj: 0,
					Jun: 0,
					Jul: 0,
					Aug: 0,
					Sep: 0,
					Okt: 0,
					Nov: 0,
					Dec: 0,
				},
			],
		];

		const wb = XLSX.utils.book_new();
		const data: any[] = [];
		data.push(this.template);
		const ws = XLSX.utils.json_to_sheet(arr[0]);
		XLSX.utils.book_append_sheet(wb, ws, '2022');
		XLSX.writeFile(wb, 'budget_template.xlsx');
	}

	applyFilter(event: Event) {
		const filterValue = (event.target as HTMLInputElement).value;
		this.searchText = filterValue;
		this.dataSource.filter = filterValue.trim().toLowerCase();
		this.resultsLength = this.dataSource.filteredData.length;
	}
}
